// Trusler og hændelseskort. Data og adfærd. Alle varsles, før de rammer, og alle har et modtræk:
//   storm: forstærk masterne (montør)          gravko og ankerslæb: tap figuren væk
//   naboprotest: kabel, borgermøde eller byg   spidsbølge: smart opladning eller rid den af
//   hedebølge: planlæg køling eller rid den af datacenter-kø: reservér kapacitet eller vent
// Kortene står i varslet. Svarer ingen, vælger kortet selv sit standardsvar, når tiden er gået.

import { clockFull } from './cities';
import { dispatch, nearestIdle } from './crews';
import { difficulty } from './difficulty';
import { markDirty, startEmergency, lineArea } from './flow';
import { cutNodes, dist, lineById, lineMid, nodeById, pointSegDist, PROTEST_KM, segmentInBand, updateShields } from './grid';
import { DUO_BONUS, duoCity, duoLine } from './duo';
import { nextInt, shuffled } from './rng';
import { fcrActive } from './effects';
import type { Area, CardOption, GridNode, Line, PlannedEvent, SimEvent, State, Threat, ThreatKind } from './types';

/** Kinds der virker nu. Resten af menuen er data til senere milepæle. */
export const IMPLEMENTED: ThreatKind[] = ['storm', 'digger', 'anchor', 'protest', 'spike', 'heatwave', 'dc_rush', 'dunkelflaute', 'cable_half', 'plant_trip'];
export const STORM_HALF_KM = 18;
/** Hvor længe figuren bliver på kortet efter nedslaget. */
export const AFTER_TICKS = 40;
/** Et hændelseskort står i 12 s, før det vælger sit standardsvar. */
export const CARD_TICKS = 120;
/** Spidsbølgen varer 15 s, hedebølgen 20 s, og borgermødet holder ledningen i 20 s. */
export const SPIKE_TICKS = 150;
export const HEAT_TICKS = 200;
export const MEETING_TICKS = 200;
/** Smart opladning spreder spidsbølgen: halvt så stor, men dobbelt så lang. */
export const SMART_SPIKE_TICKS = 2 * SPIKE_TICKS;
/** "Hold den ud" gennem en spids- eller hedebølge, uden at en ramt by mangler strøm: +3 kr. */
export const RIDE_BONUS = 3;
/** Spidsbølgens ekstra forbrug i procent efter styrke 1, 2 og 3. */
export const SPIKE_PCT: readonly number[] = [0, 30, 40, 50];
/** Spidsbølgen rammer byerne inden for 40 km af sit centrum (kan scriptes bredere). */
export const SPIKE_KM = 40;
// Hedebølgens tal står hos dem, der bruger dem: luftledninger 75 % (grid.ts), vind 60 % og sol 85 %
// (state.ts), og byerne bruger 110 % (cities.ts).
/** Datacenter-kø: højst tre kunder pr. rush. Reserveret kapacitet giver dem 30 s ro, ellers 10 s. */
export const RUSH_MAX = 3;
export const RUSH_GRACE_RESERVE = 300;
export const RUSH_GRACE_WAIT = 100;
/** Naboprotest "byg alligevel": byens mørkeur får 30 % oveni. */
export const PROTEST_BUILD_CLOCK_PM = 300;

/** Pris i kr for hvert kortsvar. Standardsvarene er gratis. */
export const CARD_COST: Record<CardOption, number> = { cable: 4, meeting: 0, build: 0, smart: 2, ride: 0, plan: 3, reserve: 5, wait: 0 };

/** Kortets svarmuligheder (anbefalet først) og standardsvar. */
export const CARDS: Partial<Record<ThreatKind, { options: CardOption[]; deflt: CardOption }>> = {
  protest: { options: ['cable', 'meeting', 'build'], deflt: 'meeting' },
  spike: { options: ['smart', 'ride'], deflt: 'ride' },
  heatwave: { options: ['plan', 'ride'], deflt: 'ride' },
  dc_rush: { options: ['reserve', 'wait'], deflt: 'wait' },
};

/** Kan figuren tappes væk? */
export function isShooable(kind: ThreatKind): boolean {
  return kind === 'digger' || kind === 'anchor' || kind === 'smaa';
}

/** At jage en småt-gæst væk giver 1 kr. */
export const SMAA_BONUS = 1;

/** Er hændelsen et kort med svarmuligheder? */
export function isCard(kind: ThreatKind): boolean {
  return CARDS[kind] !== undefined;
}

/** Har hændelsen et modtræk, spilleren kan tappe (figur eller kort)? Direktøren sørger for mindst én pr. år fra bane 2. */
export function isTappable(kind: ThreatKind): boolean {
  return isShooable(kind) || isCard(kind);
}

/** Luftledninger i stormens bånd, som stormen kan vælte. */
export function stormLines(state: State, trackY: number, half: number): Line[] {
  const out: Line[] = [];
  for (const l of state.lines) {
    if (l.status !== 'ok' || l.medium !== 'air') continue;
    const a = nodeById(state, l.a);
    const b = nodeById(state, l.b);
    if (!a || !b) continue;
    if (segmentInBand(a, b, trackY - half, trackY + half)) out.push(l);
  }
  return out;
}

export function diggerTargets(state: State): Line[] {
  return state.lines.filter((l) => l.status === 'ok' && l.medium === 'cable');
}

export function anchorTargets(state: State): Line[] {
  return state.lines.filter((l) => l.status === 'ok' && (l.medium === 'sea' || l.medium === 'hvdc'));
}

function liveCity(n: GridNode): boolean {
  return n.kind === 'city' && n.active && !n.phantom && n.city !== undefined;
}

/** Den by, en luftledning generer: en by den ender i, ellers den nærmeste inden for 8 km. */
export function protestCity(state: State, line: Line): GridNode | undefined {
  const a = nodeById(state, line.a);
  const b = nodeById(state, line.b);
  if (!a || !b) return undefined;
  if (liveCity(a)) return a;
  if (liveCity(b)) return b;
  let best: GridNode | undefined;
  let bestD = PROTEST_KM;
  for (const n of state.nodes) {
    if (!liveCity(n)) continue;
    const d = pointSegDist(n, a, b);
    if (d <= bestD) {
      best = n;
      bestD = d;
    }
  }
  return best;
}

/** Ender luftledningen i en by? Så er det den bys naboer, der protesterer (ikke en by, den går forbi). */
function endsInCity(state: State, l: Line): boolean {
  const a = nodeById(state, l.a);
  const b = nodeById(state, l.b);
  return (!!a && liveCity(a)) || (!!b && liveCity(b));
}

/**
 * Den luftledning en naboprotest gælder: spillerens nyeste hele luftledning ved en by, som ingen
 * protest handler om eller har handlet om i banen. Borgermøde-pakken fjerner protesterne resten af
 * banen. Spor: tillid (IGN runde 4): en ledning, der ender i en by, går forud for en, der kun går
 * forbi, så protesten nævner en ledning, som rører byen; og den samme ledning protesteres der aldrig
 * mod to gange ("Horsens–Give Vind to gange i én Dagens vagt").
 */
export function protestLine(state: State, wanted?: string): Line | undefined {
  if (state.perks.townMeeting) return undefined;
  const done = state.director.protested ?? [];
  let best: Line | undefined;
  let bestEnds = false;
  for (const l of state.lines) {
    if (l.owner === 'level' || l.fixed || l.status !== 'ok' || l.medium !== 'air') continue;
    if (done.includes(l.id) || state.threats.some((t) => t.kind === 'protest' && t.targetLine === l.id)) continue;
    if (!protestCity(state, l)) continue;
    if (wanted !== undefined && l.id === wanted) return l;
    const ends = endsInCity(state, l);
    if (!best || (ends && !bestEnds) || (ends === bestEnds && (l.builtTick > best.builtTick || (l.builtTick === best.builtTick && l.id > best.id)))) {
      best = l;
      bestEnds = ends;
    }
  }
  return wanted !== undefined ? undefined : best;
}

// --- Bane 5 og 6: vindstille, kabelskade og værksnedbrud --------------------------------------------

/** Vindstillen varer 20 s, kabelskaden 20 s, og et værk er ude i 15 s efter et nedbrud (reserverne tager over). */
export const DUNKEL_TICKS = 200;
export const CABLE_HALF_TICKS = 200;
export const TRIP_TICKS = 150;
/** Nettet kobler en by fra i 10 s, når frekvensen falder under 49,0 Hz. */
export const UFLS_TICKS = 100;
/** Tærskler i centihertz: under 49,00 Hz kobler nettet selv fra; over 49,50 Hz holdt nettet roligt. */
export const UFLS_CHZ = 4900;
export const CALM_CHZ = 4950;
/** Så mange MW forsvinder ved et værksnedbrud efter styrke 1, 2 og 3 (højst værkets navneplade). */
export const TRIP_MW: readonly number[] = [0, 400, 600, 800];
/**
 * Spillets inerti (DESIGN.md "Frekvens og inerti"): I = 1.500 i DK1 og 1.000 i DK2, +1.000 pr. andet
 * kørende kraftværk og +1.500 pr. svinghjul (synkronkompensator). Dykket i Hz er 3 x udækket MW / I.
 */
export const INERTIA_BASE: Record<Area, number> = { DK1: 1500, DK2: 1000 };
export const FLYWHEEL_INERTIA = 1500;
export const PLANT_INERTIA = 1000;
/** Dykkets skala: centihertz = NADIR_SCALE x udækket MW / I. */
export const NADIR_SCALE = 300;
/** Lynreserven dækker 500 MW af tabet med det samme, og et batteri dækker sin egen effekt. */
export const FCR_COVER = 500;

/** Kabelskaden rammer den største faste forbindelse (udlandskabel eller Storebælt). */
export function cableHalfTarget(state: State): Line | undefined {
  let best: Line | undefined;
  for (const l of state.lines) {
    if (!l.fixed || l.status !== 'ok' || l.fixedCapMw <= 0) continue;
    if (state.threats.some((t) => t.kind === 'cable_half' && t.phase !== 'done' && t.targetLine === l.id)) continue;
    if (!best || l.fixedCapMw > best.fixedCapMw || (l.fixedCapMw === best.fixedCapMw && l.id < best.id)) best = l;
  }
  return best;
}

/** Værker, der kan falde ud: aktive kraftværker, som ikke allerede er ude. */
export function tripTargets(state: State): GridNode[] {
  return state.nodes.filter(
    (n) => n.kind === 'plant' && n.active && n.producer !== undefined && !state.threats.some((t) => t.kind === 'plant_trip' && t.phase !== 'done' && t.nodes?.[0] === n.id),
  );
}

/** Spillets inerti i prisområdet lige nu, uden værket `except` (det, der faldt ud). */
export function inertia(state: State, area: Area, except = ''): number {
  let i = INERTIA_BASE[area];
  for (const n of state.nodes) {
    if (n.area !== area || !n.active) continue;
    i += n.flywheels * FLYWHEEL_INERTIA;
    if (n.kind === 'plant' && n.id !== except && (n.producer?.availMw ?? 0) > 0) i += PLANT_INERTIA;
  }
  return i;
}

/** MW af tabet, der dækkes med det samme: batterier i prisområdet og Lynreserven. */
export function fastCover(state: State, area: Area): number {
  let mw = fcrActive(state) ? FCR_COVER : 0;
  for (const n of state.nodes) if (n.area === area && n.active && n.battery && n.battery.store > 0) mw += n.battery.mw;
  return mw;
}

/** Frekvensens bund i centihertz, hvis `dP` MW forsvinder i prisområdet nu. */
export function predictNadir(state: State, area: Area, dP: number, except = ''): number {
  const gap = Math.max(0, dP - fastCover(state, area));
  return 5000 - Math.round((NADIR_SCALE * gap) / inertia(state, area, except));
}

/** Kan hændelsen overhovedet gøre noget lige nu? */
export function hasTarget(state: State, kind: ThreatKind): boolean {
  if (kind === 'cable_half') return cableHalfTarget(state) !== undefined;
  if (kind === 'plant_trip') return tripTargets(state).length > 0;
  if (kind === 'digger') return diggerTargets(state).length > 0;
  if (kind === 'anchor') return anchorTargets(state).length > 0;
  if (kind === 'protest') return protestLine(state) !== undefined;
  if (kind === 'spike') return state.nodes.some(liveCity);
  if (kind === 'dc_rush') return state.rushPool.length > 0;
  if (kind === 'duo') return duoLine(state) !== undefined;
  return true;
}

export function activeThreats(state: State): number {
  let n = 0;
  for (const t of state.threats) if (t.phase !== 'done' && t.kind !== 'smaa') n++;
  return n;
}

/** Start varslet for en planlagt hændelse. Returnerer null, hvis den intet mål har (den udgår). */
export function startThreat(state: State, ev: PlannedEvent, defaultStrength: number, events: SimEvent[]): Threat | null {
  const rng = state.rng.events;
  const hitAt = Math.max(ev.hitAt, state.clockTick + ev.warnTicks);
  const t: Threat = {
    id: `T${state.nextThreatId}`,
    kind: ev.kind,
    phase: 'warning',
    scripted: ev.scripted,
    warnAt: state.clockTick,
    hitAt,
    endAt: hitAt + AFTER_TICKS,
    trackY: 0,
    trackHalf: 0,
    strength: ev.strength ?? defaultStrength,
    targetLine: '',
    atRisk: [],
    shooed: false,
    hits: [],
  };
  if (ev.kind === 'storm') {
    t.trackHalf = ev.trackHalf ?? STORM_HALF_KM;
    if (ev.trackY !== undefined) {
      t.trackY = ev.trackY;
    } else {
      // Sigt efter en tilfældig luftledning, så stormen betyder noget; ellers midt i kortet.
      const air = state.lines.filter((l) => l.status === 'ok' && l.medium === 'air');
      if (air.length > 0) {
        const l = air[nextInt(rng, 0, air.length - 1)] as Line;
        t.trackY = Math.round(lineMid(state, l).y + nextInt(rng, -8, 8));
      } else {
        let lo = Infinity;
        let hi = -Infinity;
        for (const n of state.nodes) if (n.active) { lo = Math.min(lo, n.y); hi = Math.max(hi, n.y); }
        t.trackY = lo <= hi ? nextInt(rng, Math.floor(lo), Math.ceil(hi)) : 0;
      }
    }
    t.atRisk = stormLines(state, t.trackY, t.trackHalf).map((l) => l.id);
  } else if (ev.kind === 'digger' || ev.kind === 'anchor') {
    const targets = ev.kind === 'digger' ? diggerTargets(state) : anchorTargets(state);
    const wanted = ev.targetLine ? targets.find((l) => l.id === ev.targetLine) : undefined;
    const target = wanted ?? (targets.length > 0 ? targets[nextInt(rng, 0, targets.length - 1)] : undefined);
    if (!target) return null;
    t.targetLine = target.id;
    t.atRisk = [target.id];
  } else if (ev.kind === 'protest') {
    const line = (ev.targetLine ? protestLine(state, ev.targetLine) : undefined) ?? protestLine(state);
    const city = line ? protestCity(state, line) : undefined;
    if (!line || !city) return null;
    t.targetLine = line.id;
    t.atRisk = [line.id];
    t.nodes = [city.id];
  } else if (ev.kind === 'spike') {
    const cities = state.nodes.filter(liveCity);
    if (cities.length === 0) return null;
    const center = cities[nextInt(rng, 0, cities.length - 1)] as GridNode;
    const radius = ev.trackHalf ?? SPIKE_KM;
    t.trackHalf = radius;
    t.nodes = cities.filter((n) => dist(n, center) <= radius).map((n) => n.id);
    t.pct = SPIKE_PCT[Math.max(1, Math.min(3, t.strength))] ?? 30;
  } else if (ev.kind === 'heatwave') {
    t.atRisk = state.lines.filter((l) => l.status === 'ok' && l.medium === 'air').map((l) => l.id);
  } else if (ev.kind === 'dc_rush') {
    if (state.rushPool.length === 0) return null;
    t.strength = Math.max(1, Math.min(RUSH_MAX, ev.strength ?? RUSH_MAX));
    // Kunderne reserveres med det samme, så to rush aldrig deler de samme pladser.
    t.nodes = state.rushPool.splice(0, t.strength);
  } else if (ev.kind === 'duo') {
    const line = duoLine(state);
    const city = line ? duoCity(state, line) : undefined;
    if (!line || !city) return null;
    t.targetLine = line.id;
    t.atRisk = [line.id];
    t.nodes = [city.id];
    t.duo = { plan: false, drift: false };
  } else if (ev.kind === 'dunkelflaute') {
    // Ingen mål: vinden og solen går ned over hele kortet.
  } else if (ev.kind === 'cable_half') {
    const wanted = ev.targetLine ? lineById(state, ev.targetLine) : undefined;
    const line = wanted && wanted.fixed && wanted.status === 'ok' ? wanted : cableHalfTarget(state);
    if (!line) return null;
    t.targetLine = line.id;
    t.atRisk = [line.id];
  } else if (ev.kind === 'plant_trip') {
    const cands = tripTargets(state);
    if (cands.length === 0) return null;
    // Et værk, der faktisk leverer, falder helst ud; ellers et hvilket som helst.
    const running = cands.filter((n) => (n.producer?.usedMw ?? 0) > 0);
    const pool = running.length > 0 ? running : cands;
    const plant = pool[nextInt(rng, 0, pool.length - 1)] as GridNode;
    t.nodes = [plant.id];
    t.area = plant.area;
    t.dP = Math.min(plant.producer?.capMw ?? 0, TRIP_MW[Math.max(1, Math.min(3, t.strength))] ?? 400);
  } else {
    return null;
  }
  const card = CARDS[ev.kind];
  if (card) t.card = { options: [...card.options], deflt: card.deflt, answer: '' };
  state.nextThreatId++;
  state.threats.push(t);
  if (t.kind === 'anchor') sendGuard(state, t, events);
  state.director.history.push(t.kind);
  if (state.director.history.length > 8) state.director.history.shift();
  if (card && !(state.director.cards ??= []).includes(t.kind)) state.director.cards.push(t.kind);
  if (t.kind === 'protest' && t.targetLine) (state.director.protested ??= []).push(t.targetLine);
  events.push({ t: 'warning', tick: state.tick, threat: t.id, kind: t.kind, hitAt: t.hitAt, atRisk: [...t.atRisk] });
  return t;
}

function breakLine(state: State, line: Line, cause: 'storm' | 'digger' | 'anchor' | 'overload', events: SimEvent[]): void {
  line.status = 'broken';
  line.halfDown = false;
  line.reinforced = false;
  line.overTicks = 0;
  line.flow = 0;
  line.loadPm = 0;
  startEmergency(state, lineArea(state, line));
  markDirty(state);
  updateShields(state);
  events.push({ t: 'line_broken', tick: state.tick, line: line.id, cause });
  for (const n of cutNodes(state, line)) events.push({ t: 'node_cut', tick: state.tick, node: n.id, kind: n.kind });
}

export { breakLine };

function hitStorm(state: State, t: Threat, events: SimEvent[]): void {
  const cands = shuffled(state.rng.events, stormLines(state, t.trackY, t.trackHalf));
  let left = t.strength;
  for (const l of cands) {
    if (left <= 0) break;
    left--;
    if (l.reinforced) {
      l.reinforced = false;
      events.push({ t: 'line_saved', tick: state.tick, line: l.id, by: 'reinforced' });
      continue;
    }
    if (l.tier === 3) {
      if (l.halfDown) {
        events.push({ t: 'line_saved', tick: state.tick, line: l.id, by: 'double' });
        continue;
      }
      l.halfDown = true;
      markDirty(state);
      t.hits.push(l.id);
      events.push({ t: 'line_half', tick: state.tick, line: l.id });
      continue;
    }
    t.hits.push(l.id);
    breakLine(state, l, 'storm', events);
  }
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [...t.hits] });
}

/**
 * Montørskibet sejler selv ud mod kablet, når en ankerbåd varsles, hvis det er ledigt. Det sejler
 * langs sin rute (ingen teleport) og jager kun båden væk, hvis det når frem før ankeret rammer.
 * Ellers må spilleren tappe båden.
 */
function sendGuard(state: State, t: Threat, events: SimEvent[]): void {
  const line = lineById(state, t.targetLine);
  if (!line) return;
  const ship = nearestIdle(state, line);
  if (ship && ship.kind === 'ship') dispatch(state, ship, line, 'guard', events);
}

/** Holder montørskibet vagt ved kablet, båden er på vej mod? */
function guarded(state: State, t: Threat): boolean {
  return state.crews.some((c) => c.task === 'guarding' && c.lineId === t.targetLine);
}

function hitTarget(state: State, t: Threat, events: SimEvent[]): void {
  const line = lineById(state, t.targetLine);
  const okMedium = t.kind === 'digger' ? line?.medium === 'cable' : line?.medium === 'sea' || line?.medium === 'hvdc';
  if (line && okMedium && line.status === 'ok') {
    t.hits.push(line.id);
    breakLine(state, line, t.kind === 'digger' ? 'digger' : 'anchor', events);
  }
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [...t.hits] });
}

/**
 * Lynet rammer den fælles alarms ledning. Begge nåede det: ledningen holder (forstærkningen tager
 * slaget), og kassen får bonussen. Én: ledningen bærer det halve, til en montør har set på den.
 * Ingen: den knækker som i en storm.
 */
function hitDuo(state: State, t: Threat, events: SimEvent[]): void {
  const d = t.duo ?? { plan: false, drift: false };
  const done = (d.plan ? 1 : 0) + (d.drift ? 1 : 0);
  const line = lineById(state, t.targetLine);
  if (line && line.status === 'ok') {
    if (line.reinforced) {
      line.reinforced = false;
      events.push({ t: 'line_saved', tick: state.tick, line: line.id, by: 'reinforced' });
    }
    if (done === 1) {
      line.halfDown = true;
      markDirty(state);
      t.hits.push(line.id);
      events.push({ t: 'line_half', tick: state.tick, line: line.id });
    } else if (done === 0) {
      t.hits.push(line.id);
      breakLine(state, line, 'storm', events);
    }
  }
  if (done === 2) {
    state.budget += DUO_BONUS;
    events.push({ t: 'duo_won', tick: state.tick, threat: t.id, line: t.targetLine, bonus: DUO_BONUS });
  } else {
    events.push({ t: 'duo_missed', tick: state.tick, threat: t.id, line: t.targetLine, done: done as 0 | 1 });
  }
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [...t.hits] });
}

/** Kortet er afgjort uden mere effekt: figuren går af kortet om 2 s. */
function settle(state: State, t: Threat): void {
  t.shooed = true;
  t.phase = 'active';
  t.endAt = state.clockTick + 20;
}

/** Naboprotesten afgøres i det øjeblik, nogen svarer (eller tiden løber ud). */
function resolveProtest(state: State, t: Threat, answer: CardOption, cost: number, events: SimEvent[]): void {
  const line = lineById(state, t.targetLine);
  if (answer === 'cable') {
    if (line && line.medium === 'air') {
      line.medium = 'cable';
      line.reinforced = false;
      line.spent += cost;
      events.push({ t: 'line_buried', tick: state.tick, line: line.id, cost });
    }
    settle(state, t);
  } else if (answer === 'build') {
    const city = t.nodes?.[0] ? nodeById(state, t.nodes[0]) : undefined;
    if (city?.city) {
      const full = clockFull(state);
      city.city.happyThisYear = false;
      city.city.clock = Math.min(full - 1, city.city.clock + Math.round((full * PROTEST_BUILD_CLOCK_PM) / 1000));
    }
    settle(state, t);
  } else {
    // Borgermøde: ledningen står stille, mens mødet kører.
    t.phase = 'active';
    t.endAt = state.clockTick + MEETING_TICKS;
    if (line) {
      markDirty(state);
      events.push({ t: 'line_held', tick: state.tick, line: line.id, ticks: MEETING_TICKS });
    }
  }
}

/** Kan dette svar gives på kortet lige nu (uden at se på budgettet)? */
export function cardOpen(t: Threat): boolean {
  return t.card !== undefined && t.card.answer === '' && t.phase === 'warning';
}

/** Svar på et kort. Kalderen har tjekket, at svaret er lovligt og betalt. Returnerer prisen. */
export function answerCard(state: State, t: Threat, answer: CardOption, auto: boolean, events: SimEvent[]): number {
  if (!t.card) return 0;
  const cost = auto ? 0 : CARD_COST[answer];
  t.card.answer = answer;
  state.budget -= cost;
  events.push({ t: 'card_answered', tick: state.tick, threat: t.id, kind: t.kind, answer, auto, cost });
  if (t.kind === 'protest') resolveProtest(state, t, answer, cost, events);
  return cost;
}

/** Kortets effekt rammer, når varslet er slut. */
function hitCard(state: State, t: Threat, events: SimEvent[]): void {
  if (t.kind === 'spike') {
    t.endAt = state.clockTick + (t.card?.answer === 'smart' ? SMART_SPIKE_TICKS : SPIKE_TICKS);
  } else if (t.kind === 'heatwave') {
    t.endAt = state.clockTick + HEAT_TICKS;
  } else if (t.kind === 'dc_rush') {
    const pct = difficulty(state.difficulty).phantomPct;
    const reserved = t.card?.answer === 'reserve';
    const appeared: string[] = [];
    for (const id of t.nodes ?? []) {
      // Terningen kastes for hver kunde uanset svaret, så svaret ikke flytter resten af banens rng.
      const phantom = nextInt(state.rng.events, 0, 99) < pct;
      const n = nodeById(state, id);
      if (!n || n.active) continue;
      if (phantom && reserved) continue; // reservationen afslørede fantomet: det dukker aldrig op
      n.phantom = phantom;
      n.active = true;
      n.activeSince = state.tick;
      n.appearedAt = state.clockTick;
      if (n.city) n.city.graceTicks = reserved ? RUSH_GRACE_RESERVE : RUSH_GRACE_WAIT;
      appeared.push(n.id);
      events.push({ t: 'node_appeared', tick: state.tick, node: n.id, kind: n.kind });
    }
    if (appeared.length > 0) updateShields(state);
  }
  markDirty(state);
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [] });
}

/** Vindstille og kabelskade: effekten kører, til tiden er gået. Modtrækket er Norge, batterier og reserver. */
function hitLong(state: State, t: Threat, events: SimEvent[]): void {
  t.endAt = state.clockTick + (t.kind === 'dunkelflaute' ? DUNKEL_TICKS : CABLE_HALF_TICKS);
  markDirty(state);
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: t.kind === 'cable_half' ? [t.targetLine] : [] });
}

/**
 * Værksnedbrud: værket er ude i 30 s, og frekvensen dykker i prisområdet. Svinghjul, kørende værker,
 * batterier og Lynreserven tager dykket. Under 49,0 Hz kobler nettet selv den største by fra i 10 s
 * (UFLS), og byens ur får et hug. Over 49,5 Hz holdt nettet roligt: +3 kr.
 */
function hitTrip(state: State, t: Threat, events: SimEvent[]): void {
  const id = t.nodes?.[0] ?? '';
  const plant = nodeById(state, id);
  t.endAt = state.clockTick + TRIP_TICKS;
  if (!plant || !plant.active) {
    t.nadir = 5000;
    events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [] });
    return;
  }
  const area = plant.area;
  t.area = area;
  const dP = t.dP ?? 0;
  const nadir = predictNadir(state, area, dP, id);
  t.nadir = nadir;
  let shed: string | undefined;
  if (nadir < UFLS_CHZ) {
    let big: GridNode | undefined;
    for (const n of state.nodes) {
      if (n.area !== area || !liveCity(n) || !n.city || n.city.out) continue;
      if (!big || n.city.demandMw > (big.city?.demandMw ?? 0) || (n.city.demandMw === big.city?.demandMw && n.id < big.id)) big = n;
    }
    if (big?.city) {
      shed = big.id;
      t.shedNode = big.id;
      t.shedUntil = state.clockTick + UFLS_TICKS;
      big.city.happyThisYear = false;
      const full = clockFull(state);
      big.city.clock = Math.min(full - 1, Math.max(big.city.clock, Math.round(full / 2)));
    }
  } else if (nadir >= CALM_CHZ) {
    state.budget += RIDE_BONUS;
    events.push({ t: 'ride_bonus', tick: state.tick, threat: t.id, kind: t.kind, amount: RIDE_BONUS });
  }
  markDirty(state);
  events.push({ t: 'freq_dip', tick: state.tick, threat: t.id, node: id, area, dP, nadir, ...(shed ? { shed } : {}) });
  events.push({ t: 'threat_hit', tick: state.tick, threat: t.id, kind: t.kind, lines: [] });
}

/** Spids- eller hedebølge, der blev redet af med "Hold den ud". Vindstillen rides altid af. */
function rideable(t: Threat): boolean {
  if (t.kind === 'dunkelflaute') return true;
  return (t.kind === 'spike' || t.kind === 'heatwave') && t.card?.answer === 'ride';
}

/** Er en by, bølgen rammer, kommet 5 % ind i sit mørkeur? Spidsbølgen rammer sine byer, hedebølgen alle. */
function waveStrains(state: State, t: Threat): boolean {
  const limit = clockFull(state) / 20;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom) continue;
    if (t.kind === 'spike' && !t.nodes?.includes(n.id)) continue;
    if (c.clock >= limit || c.darkTicks > 0) return true;
  }
  return false;
}

/** Ét tick for alle trusler. */
export function updateThreats(state: State, events: SimEvent[]): void {
  for (const t of state.threats) {
    if (t.kind === 'smaa') continue; // småt.ts styrer gæsterne
    if (t.phase === 'warning') {
      if (t.kind === 'storm' && state.clockTick % 10 === 0) {
        t.atRisk = stormLines(state, t.trackY, t.trackHalf).map((l) => l.id);
      }
      if (t.kind === 'anchor' && !t.shooed && guarded(state, t)) {
        shoo(state, t, events);
        continue;
      }
      if (state.clockTick >= t.hitAt) {
        if (t.card && t.card.answer === '') {
          answerCard(state, t, t.card.deflt, true, events);
          if (t.phase !== 'warning') continue; // protesten afgjorde sig selv
        }
        t.phase = 'active';
        if (t.shooed) continue;
        if (t.kind === 'storm') hitStorm(state, t, events);
        else if (t.kind === 'duo') hitDuo(state, t, events);
        else if (t.kind === 'dunkelflaute' || t.kind === 'cable_half') hitLong(state, t, events);
        else if (t.kind === 'plant_trip') hitTrip(state, t, events);
        else if (t.card) hitCard(state, t, events);
        else hitTarget(state, t, events);
      }
    } else if (t.phase === 'active') {
      if (rideable(t) && !t.strained && waveStrains(state, t)) t.strained = true;
      if (state.clockTick >= t.endAt) {
        t.phase = 'done';
        if (t.card || t.kind === 'dunkelflaute' || t.kind === 'cable_half' || t.kind === 'plant_trip') markDirty(state);
        state.stats.threatsFaced.push({ kind: t.kind, clean: t.hits.length === 0 });
        if (rideable(t) && !t.strained) {
          state.budget += RIDE_BONUS;
          events.push({ t: 'ride_bonus', tick: state.tick, threat: t.id, kind: t.kind, amount: RIDE_BONUS });
        }
        events.push({ t: 'threat_done', tick: state.tick, threat: t.id, kind: t.kind });
      }
    }
  }
  if (state.threats.some((t) => t.phase === 'done')) state.threats = state.threats.filter((t) => t.phase !== 'done');
}

/** Tap gravkoen eller båden væk. */
export function shoo(state: State, t: Threat, events: SimEvent[]): void {
  t.shooed = true;
  t.phase = 'active';
  t.endAt = state.clockTick + 20;
  if (t.kind === 'smaa') {
    // Gæsten flyver sin vej: ledningen er hel igen, og tappet giver en krone.
    state.budget += SMAA_BONUS;
    markDirty(state);
    events.push({ t: 'threat_shooed', tick: state.tick, threat: t.id, kind: t.kind, amount: SMAA_BONUS });
    return;
  }
  events.push({ t: 'threat_shooed', tick: state.tick, threat: t.id, kind: t.kind });
}
